import axios from 'axios';
import { validateCenterData, filterByCenterId, logValidation } from '../utils/dataValidator';
// Error handler utilities available if needed
// import { handleError, isRetryableError, ErrorTypes } from './errorHandler';
import { networkStatus } from './networkStatus';
import { offlineQueue } from '../lib/offlineQueue';
import { trackEvent } from '../lib/telemetry';
import { isNative } from '../utils/platform';
import { API_BASE_URL } from './apiBase';
import { navigateTo } from './navigation';
import * as tokenStore from './tokenStore';

// =============================================================================
// QUEUEABLE ENDPOINTS (Phase 1.8)
// =============================================================================
//
// Requests matching these patterns can be persisted to the IndexedDB offline
// queue when the network is down, and replayed on reconnect. Limited to a
// known-safe allowlist — we don't blindly queue every mutation because the
// queue can't reconcile server-side conflicts on arbitrary data.
//
// Format: { method, urlPattern: RegExp }
const QUEUEABLE_PATTERNS = [
  { method: 'POST', urlPattern: /^\/attendance\/mark(?:\?|$)/ },
  // Worker attendance punches (clock in/out, break start/end) must survive an
  // offline blip — "the worker's wifi died at the front desk". Each punch
  // carries an Idempotency-Key so an offline-then-online replay can't create a
  // duplicate session (plan §13.3 / §8.4).
  { method: 'POST', urlPattern: /^\/staff-attendance\/clock-in(?:\?|$)/ },
  { method: 'POST', urlPattern: /^\/staff-attendance\/clock-out(?:\?|$)/ },
  { method: 'POST', urlPattern: /^\/staff-attendance\/break\/start(?:\?|$)/ },
  { method: 'POST', urlPattern: /^\/staff-attendance\/break\/end(?:\?|$)/ },
];

// Request headers that must be persisted with a queued offline request so they
// survive replay (e.g. the idempotency key that dedupes a replayed punch).
const QUEUEABLE_HEADER_ALLOWLIST = ['Idempotency-Key'];

function isQueueable(config) {
  const method = (config.method || '').toUpperCase();
  const url = config.url || '';
  return QUEUEABLE_PATTERNS.some(p => p.method === method && p.urlPattern.test(url));
}


// =============================================================================
// ENTERPRISE API CONFIGURATION
// =============================================================================

const CONFIG = {
  // Timeout settings
  timeout: 30000, // 30 seconds default timeout

  // Retry settings
  retry: {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    retryStatusCodes: [408, 429, 500, 502, 503, 504]
  }
};

// =============================================================================
// AXIOS INSTANCE
// =============================================================================

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  }
});

// =============================================================================
// OFFLINE QUEUE INTEGRATION (Phase 1.8)
// =============================================================================
//
// Persistent IndexedDB queue lives in lib/offlineQueue.js. The api layer's job
// is just to enqueue queueable mutations when offline and trigger flush on
// reconnect.

function getAuthHeader() {
  const token = tokenStore.getItem('authToken');
  return token ? `Bearer ${token}` : null;
}

async function flushOfflineQueue() {
  try {
    const result = await offlineQueue.flush({
      baseUrl: API_BASE_URL,
      getAuthHeader,
    });
    if (result.ok > 0 || result.failed > 0) {
      console.log(`[API] Flushed ${result.ok} queued request(s) (${result.failed} failed)`);
      trackEvent('offline.flushed', { ok: result.ok, failed: result.failed });
    }
  } catch (err) {
    console.warn('[API] Offline queue flush failed:', err);
  }
}

// Try an opportunistic flush on app boot (in case the previous session left
// items in the queue and we're already online).
if (typeof window !== 'undefined') {
  if (navigator.onLine !== false) {
    // Defer until after first tick so the rest of the app boots cleanly.
    setTimeout(flushOfflineQueue, 1500);
  }
}

// Subscribe to network status changes
networkStatus.subscribe(({ isOnline, wasOffline }) => {
  if (isOnline && wasOffline) {
    console.log('[API] Back online, flushing offline queue...');
    flushOfflineQueue();
  }
});

// =============================================================================
// RETRY LOGIC
// =============================================================================

/**
 * Calculate delay for retry attempt using exponential backoff with jitter
 */
function calculateRetryDelay(attempt) {
  const { baseDelay, maxDelay } = CONFIG.retry;
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return Math.min(exponentialDelay + jitter, maxDelay);
}

/**
 * Check if we should retry this error
 */
function shouldRetry(error, attempt) {
  if (error.config?.noRetry) return false;

  if (attempt >= CONFIG.retry.maxRetries) return false;

  // Don't retry if we're offline
  if (!networkStatus.getStatus()) return false;

  // Retry on network errors
  if (!error.response) return true;

  // Retry on specific status codes
  const status = error.response?.status;
  return CONFIG.retry.retryStatusCodes.includes(status);
}

/**
 * Sleep for specified duration
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// =============================================================================
// REQUEST INTERCEPTOR
// =============================================================================

api.interceptors.request.use(
  async (config) => {
    // Phase 1.8 — offline handling.
    if (!networkStatus.getStatus()) {
      // GETs: fail fast — the caller decides how to render stale state.
      if (config.method?.toLowerCase() === 'get') {
        const error = new Error('You are offline. Please check your connection.');
        error.code = 'OFFLINE';
        throw error;
      }
      // Mutations on the queueable allowlist: persist + return synthetic 202.
      if (isQueueable(config)) {
        try {
          // Carry forward only allowlisted headers (e.g. Idempotency-Key) so a
          // replayed punch dedupes server-side. The auth header is added at
          // flush time from the current session, not persisted here.
          const persistedHeaders = {};
          for (const name of QUEUEABLE_HEADER_ALLOWLIST) {
            const value = config.headers?.[name];
            if (value != null) persistedHeaders[name] = value;
          }
          await offlineQueue.enqueue({
            method: (config.method || 'POST').toUpperCase(),
            url: config.url,
            body: config.data ?? null,
            headers: persistedHeaders,
          });
          trackEvent('offline.queued', {
            method: (config.method || 'POST').toUpperCase(),
            url: config.url,
          });
          // Throw a tagged error that the caller (and the response interceptor)
          // can detect. We deliberately don't `return` here because axios would
          // proceed with the real HTTP request.
          const queued = new Error('Request queued for sync');
          queued.code = 'QUEUED_OFFLINE';
          queued.config = config;
          throw queued;
        } catch (err) {
          if (err.code === 'QUEUED_OFFLINE') throw err;
          // Queue persistence itself failed — fall through to the offline error.
          console.warn('[API] Failed to enqueue offline request:', err);
        }
      }
    }

    // Add auth token
    const token = tokenStore.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request ID for tracking
    config.metadata = {
      requestId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      startTime: Date.now()
    };

    // Development logging (reduced verbosity)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// RESPONSE INTERCEPTOR
// =============================================================================

api.interceptors.response.use(
  (response) => {
    // Log response time in development
    if (process.env.NODE_ENV === 'development' && response.config.metadata?.startTime) {
      const duration = Date.now() - response.config.metadata.startTime;
      console.log(`[API] ${response.config.url} completed in ${duration}ms`);
    }

    // Skip processing for binary responses (like PDF blobs)
    if (response.data instanceof Blob) {
      return response;
    }

    const centerId = tokenStore.getItem('centerId');

    // Extract data from response (handle multiple formats)
    let data;
    if (Array.isArray(response.data)) {
      data = response.data;
    } else if (response.data?.data) {
      data = response.data.data;
    } else {
      data = response.data;
    }

    // Validate centerId for responses that contain center-specific data
    if (data && typeof data === 'object' && centerId) {
      if (!validateCenterData(data, centerId)) {
        console.warn('[API] Data validation failed - filtering mismatched data');
        const filteredData = filterByCenterId(data, centerId);
        logValidation(`${response.config.url} - FILTERED`, centerId, false,
          Array.isArray(filteredData) ? filteredData.length : 1);

        if (Array.isArray(response.data)) {
          return filteredData;
        } else if (response.data?.data) {
          return { ...response.data, data: filteredData };
        }
        return filteredData;
      }
    }

    // Return extracted data
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data?.data) {
      const arr = response.data.data;
      // Preserve pagination metadata that would otherwise be lost when we
      // unwrap the envelope to its inner array. Without this, list pages can
      // only see the current page's length and assume that's the grand total,
      // capping the UI at one page. Attached non-enumerably so iteration,
      // spreads, and JSON output are unaffected; consumers read
      // `result.pagination` / `result.pagination.total`.
      if (Array.isArray(arr) && response.data.pagination) {
        try {
          Object.defineProperty(arr, 'pagination', {
            value: response.data.pagination, enumerable: false, configurable: true,
          });
        } catch (_) { /* frozen array — ignore */ }
      }
      return arr;
    }
    return response.data;
  },
  async (error) => {
    const config = error.config;

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;

    // Check if we should retry
    if (shouldRetry(error, config.__retryCount)) {
      config.__retryCount++;
      const delay = calculateRetryDelay(config.__retryCount - 1);

      console.log(`[API] Retry ${config.__retryCount}/${CONFIG.retry.maxRetries} for ${config.url} in ${Math.round(delay)}ms`);

      await sleep(delay);
      return api.request(config);
    }

    // Handle specific error codes
    const status = error.response?.status;

    if (status === 401) {
      const requestUrl = error.config?.url || '';
      const errorCode = error.response?.data?.code;
      // Don't redirect on login endpoint - let LoginPage handle the error
      if (!requestUrl.includes('/auth/login')) {
        if (errorCode === 'MEMBERSHIP_REVOKED') {
          console.warn('[API] Membership revoked — logging out');
        } else {
          console.error('[API] 401 Unauthorized - clearing auth state');
        }
        // Use dynamic import to avoid circular dependency
        const { default: useAuthStore } = await import('../store/authStore');
        useAuthStore.getState().logout();
        // Redirect to a clean login. On web a full document load is intentional
        // (fresh page, user sees the error code). On native there is no SPA
        // fallback server, so route through react-router instead of a hard
        // window.location that would white-screen the WebView.
        const onLogin = typeof window !== 'undefined' &&
          (window.location.pathname.startsWith('/login') || (window.location.hash || '').startsWith('#/login'));
        if (typeof window !== 'undefined' && !onLogin) {
          const reason = errorCode === 'MEMBERSHIP_REVOKED' ? '?reason=membership_revoked' : '';
          if (isNative) {
            navigateTo(`/login${reason}`, { replace: true });
          } else {
            window.location.assign(`/login${reason}`);
          }
        }
      }
      return Promise.reject(error.response?.data || error);
    }

    if (status === 403) {
      const message = error.response?.data?.message || '';
      const errorCode = error.response?.data?.code;
      // authenticateToken answers an expired/invalid JWT with 403 "Invalid or
      // expired token" (not 401). Without logging out here, a tab left open
      // past the token TTL polls /notifications/unread-count into 403s forever.
      if (
        errorCode === 'NO_CENTER' ||
        message.toLowerCase().includes('expired token') ||
        message.toLowerCase().includes('not assigned') ||
        message.toLowerCase().includes('authentication') ||
        message.toLowerCase().includes('tenant')
      ) {
        console.error('[API] 403 - User not assigned to center');
        const { default: useAuthStore } = await import('../store/authStore');
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }
    }

    // Return processed error
    return Promise.reject(error.response?.data || error);
  }
);

// =============================================================================
// ENHANCED API METHODS
// =============================================================================

/**
 * Make a request with custom timeout
 */
api.withTimeout = (timeout) => {
  return {
    get: (url, config = {}) => api.get(url, { ...config, timeout }),
    post: (url, data, config = {}) => api.post(url, data, { ...config, timeout }),
    put: (url, data, config = {}) => api.put(url, data, { ...config, timeout }),
    patch: (url, data, config = {}) => api.patch(url, data, { ...config, timeout }),
    delete: (url, config = {}) => api.delete(url, { ...config, timeout })
  };
};

/**
 * Make a request with no retry
 */
api.noRetry = {
  get: (url, config = {}) => api.get(url, { ...config, __retryCount: CONFIG.retry.maxRetries }),
  post: (url, data, config = {}) => api.post(url, data, { ...config, __retryCount: CONFIG.retry.maxRetries }),
  put: (url, data, config = {}) => api.put(url, data, { ...config, __retryCount: CONFIG.retry.maxRetries }),
  patch: (url, data, config = {}) => api.patch(url, data, { ...config, __retryCount: CONFIG.retry.maxRetries }),
  delete: (url, config = {}) => api.delete(url, { ...config, __retryCount: CONFIG.retry.maxRetries })
};

/**
 * Check if the API is available
 */
api.healthCheck = async () => {
  try {
    await api.get('/health', { timeout: 5000 });
    return true;
  } catch {
    return false;
  }
};

/**
 * Get current network status
 */
api.isOnline = () => networkStatus.getStatus();

// =============================================================================
// WORKER ATTENDANCE (staffAttendance) — plan §9
// =============================================================================
//
// Thin wrappers over the `/staff-attendance` backend routes. They return the
// already-unwrapped response body (the response interceptor strips the axios
// envelope), so callers get the `session` / `report` / list payload directly.
//
// Punches (clock-in/out, break start/end) are queueable: when offline the
// request interceptor persists them to the IndexedDB queue and replays on
// reconnect. Each punch carries an `Idempotency-Key` so a replay can't open a
// duplicate session.

/** Generate an idempotency key for a punch (survives offline replay). */
function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

api.attendance = {
  /** POST /staff-attendance/clock-in — open a session. Queueable. */
  clockIn: ({ source, geo } = {}) =>
    api.post('/staff-attendance/clock-in', { source, geo }, {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    }),

  /** POST /staff-attendance/clock-out — close the open session. Queueable. */
  clockOut: ({ geo } = {}) =>
    api.post('/staff-attendance/clock-out', { geo }, {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    }),

  /** POST /staff-attendance/break/start — begin a break. Queueable. */
  breakStart: () =>
    api.post('/staff-attendance/break/start', {}, {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    }),

  /** POST /staff-attendance/break/end — end the current break. Queueable. */
  breakEnd: () =>
    api.post('/staff-attendance/break/end', {}, {
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    }),

  /** GET /staff-attendance/me/today — { session|null, totals:{ todayMin, weekMin } }. */
  me: {
    today: () => api.get('/staff-attendance/me/today'),
  },

  /** GET /staff-attendance/me?from&to — the caller's own sessions in a range. */
  meRange: (from, to) =>
    api.get('/staff-attendance/me', { params: { from, to } }),

  /** POST /staff-attendance/manual — manual entry / correction request. */
  manual: (payload) => api.post('/staff-attendance/manual', payload),

  /** GET /staff-attendance/live — admin live "who's in now" board. */
  live: () => api.get('/staff-attendance/live'),

  /** GET /staff-attendance — admin paginated session list. */
  list: ({ workerId, from, to, status, page } = {}) =>
    api.get('/staff-attendance', { params: { workerId, from, to, status, page } }),

  /** PATCH /staff-attendance/:id — admin field edit or approve/reject. */
  update: (id, payload) => api.patch(`/staff-attendance/${id}`, payload),

  /**
   * GET /staff-attendance/report — monthly payroll-ready report.
   * For `format: 'csv'|'pdf'` the backend streams a file, so request a Blob.
   */
  report: ({ from, to, workerId, format } = {}) =>
    api.get('/staff-attendance/report', {
      params: { from, to, workerId, format },
      ...(format && format !== 'json' ? { responseType: 'blob' } : {}),
    }),

  /** GET /staff-attendance/policy — current center attendance policy. */
  getPolicy: () => api.get('/staff-attendance/policy'),

  /** PUT /staff-attendance/policy — update the center attendance policy. */
  updatePolicy: (payload) => api.put('/staff-attendance/policy', payload),
};

export default api;
